import {
  collection, doc, addDoc, updateDoc, deleteDoc,
  query, where, getDocs, getDoc, serverTimestamp, onSnapshot, limit,
} from 'firebase/firestore';
import { db } from './firebase';

// A ceiling on the list queries below. These were unbounded, which matters far
// more here than it looks: a project document carries its ENTIRE scene as JSON,
// so listing projects downloads every design in full just to render a row of
// names. The cap is set well above any realistic library, so no existing user
// loses a row — it only stops the worst case being unbounded.
const MAX_LIST = 200;

// ── Collections ──────────────────────────────────────────────────────────────

export async function createCollection(userId, collectionName) {
  const ref = await addDoc(collection(db, 'collections'), {
    userId,
    collectionName: collectionName.trim(),
    addDateTime: serverTimestamp(),
  });
  return ref.id;
}

export async function getUserCollections(userId) {
  const q = query(
    collection(db, 'collections'),
    where('userId', '==', userId),
    limit(MAX_LIST)
  );
  const snap = await getDocs(q);
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .sort((a, b) => {
      const ta = a.addDateTime?.toDate?.() ?? new Date();
      const tb = b.addDateTime?.toDate?.() ?? new Date();
      return tb - ta;
    });
}

export async function deleteCollection(collectionId) {
  await deleteDoc(doc(db, 'collections', collectionId));
}

// ── Projects ─────────────────────────────────────────────────────────────────

export async function createProject(userId, projectName, collectionId = null, size = 'S') {
  const ref = await addDoc(collection(db, 'projects'), {
    userId:        userId,
    collectionId:  collectionId,
    projectName:   projectName.trim(),
    size:          size, // 'S' (50x50) | 'M' (100x100) | 'L' (150x150) — dashboard grid size, chosen once at creation
    data:          '{}',
    addDateTime:   serverTimestamp(),
  });
  return ref.id;
}

export async function getUserProjects(userId) {
  const q = query(
    collection(db, 'projects'),
    where('userId', '==', userId),
    limit(MAX_LIST)
  );
  const snap = await getDocs(q);
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .sort((a, b) => {
      const ta = a.addDateTime?.toDate?.() ?? new Date();
      const tb = b.addDateTime?.toDate?.() ?? new Date();
      return tb - ta;
    });
}

export async function getProjectsByCollection(userId, collectionId) {
  const q = query(
    collection(db, 'projects'),
    where('userId', '==', userId),
    where('collectionId', '==', collectionId),
    limit(MAX_LIST)
  );
  const snap = await getDocs(q);
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .sort((a, b) => {
      const ta = a.addDateTime?.toDate?.() ?? new Date();
      const tb = b.addDateTime?.toDate?.() ?? new Date();
      return tb - ta;
    });
}

export async function getProject(projectId) {
  const snap = await getDoc(doc(db, 'projects', projectId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

// Live-subscribe to a project doc — `cb` fires with the latest data (or null if
// it's gone) on every change. Returns an unsubscribe function. Used by the
// shared viewer so an editor's saved changes appear for everyone in real time.
export function subscribeProject(projectId, cb) {
  return onSnapshot(
    doc(db, 'projects', projectId),
    (snap) => cb(snap.exists() ? { id: snap.id, ...snap.data() } : null),
    (err) => console.error('[project subscribe] failed:', err),
  );
}

export async function deleteProject(projectId) {
  await deleteDoc(doc(db, 'projects', projectId));
}

export async function renameProject(projectId, projectName) {
  await updateDoc(doc(db, 'projects', projectId), { projectName: projectName.trim() });
}

// Save the cost-estimate. `price` = number total, `amount` = currency code
// ('USD' | 'RUB' | 'AMD'), `priceData` = JSON string of the full breakdown
// (rates + per-item prices) so the estimate can be reopened and edited later.
export async function setProjectPrice(projectId, price, amount, priceData) {
  await updateDoc(doc(db, 'projects', projectId), { price, amount, priceData: priceData ?? null });
}

// Duplicate a project — new doc with the same scene data/size/collection and a
// "… copy" name. `source` is a loaded project object (must include its `data`).
export async function duplicateProject(userId, source) {
  const ref = await addDoc(collection(db, 'projects'), {
    userId:       userId,
    collectionId: source.collectionId ?? null,
    projectName:  `${source.projectName || 'Untitled'} copy`,
    size:         source.size || 'S',
    data:         source.data || '{}',
    addDateTime:  serverTimestamp(),
  });
  return ref.id;
}

// ── Save scene data (called from 3D editor) ──────────────────────────────────

export async function saveProjectToWeb(projectId, sceneData) {
  await updateDoc(doc(db, 'projects', projectId), {
    data: sceneData,
    lastSaved: serverTimestamp(),
  });
}

// ── Public sharing ────────────────────────────────────────────────────────────
// Flip a project's `shared` flag. When true, the Firestore security rules (see
// firestore-share.rules) allow anyone — signed in or not — to READ this one
// project, so a /share/:userId/:projectId link works for a logged-out viewer.
// Only reads are opened up; writes still require being the owner.
export async function setProjectShared(projectId, shared) {
  await updateDoc(doc(db, 'projects', projectId), { shared: !!shared });
}

// ── Sharing driven by PUBLISHING (designer profile) ──────────────────────────
// Publishing a design to a public profile has to make the project readable, or
// the /share link on its card opens nothing. That means flipping the same
// `shared` flag the manual Share button uses — which creates a problem on the
// way back out: un-publishing must not silently kill a share link the designer
// created separately and still relies on.
//
// `sharedByPublish` records that PUBLISHING is what turned sharing on. Only
// then is it turned off again when the design is unpublished. A project the
// user shared by hand is left exactly as they set it.
export async function shareProjectForPublish(projectId, perms = {}) {
  const snap = await getDoc(doc(db, 'projects', projectId));
  const alreadyShared = snap.exists() && snap.data().shared === true;
  await updateDoc(doc(db, 'projects', projectId), {
    shared: true,
    // Don't claim ownership of a share the user set up themselves.
    ...(alreadyShared ? {} : { sharedByPublish: true }),
    // WHAT A VIEWER MAY DO, written HERE as well as on the gallery item.
    // The share page already reads this document to render the scene, so
    // keeping the two flags on it means deciding what to offer costs no extra
    // read — where looking them up on publishedDesigns would need a query by
    // projectId on every open.
    allowCopy: perms.allowCopy === true,
    allowDownload: perms.allowDownload === true,
  });
}

// Reverses the above — but ONLY if publishing is what enabled sharing.
export async function unshareProjectAfterUnpublish(projectId) {
  const snap = await getDoc(doc(db, 'projects', projectId));
  if (!snap.exists() || snap.data().sharedByPublish !== true) return;
  await updateDoc(doc(db, 'projects', projectId), {
    shared: false,
    sharedByPublish: false,
  });
}

// Flag whether a shared project accepts edits from an authorised editor (one
// who redeemed a share code for it). The /share viewer reads this to decide
// whether to check edit access at all.
export async function setProjectShareEditable(projectId, editable) {
  await updateDoc(doc(db, 'projects', projectId), { shareEditable: !!editable });
}
